import { MultiselectProps } from '@amzn/awsui-components-core';
import { v4 as uuidv4 } from 'uuid';
import { fetchPageSummary } from 'src/api/app-sync-events-service';
import { ApiErrorHandler, ApiResponseError, REQUEST_TIMEOUT, createTimeoutController, handleTimeoutError } from 'src/utilities/ApiErrorHandler';
import { logger } from 'src/logger';
import { VALID_PROMPT_KEYWORDS, RESTRICTED_PROMPT_KEYWORDS, AI_OPTIMIZATION_PROMPT_TEMPLATE, ENHANCE_SUMMARY_PROMPT_TEMPLATE } from '../constants';
import { parseChartData, barLineChartTransformer, pieChartTransformer } from './customChartUtils';
import { ChartData, ChartProps } from '../types/interfaces';

export const getPlaceholderText = (pageType: string): string => {
  if (pageType.toLowerCase().includes('user')) {
    return 'Example: Analyze user engagement patterns across applications, or Which applications have the highest user activity\nGenerate Chart to highlight viewership metrics in past month';
  } else if (pageType.toLowerCase().includes('ticket')) {
    return 'Example: Focus on high-severity tickets and their resolution patterns?\nGenerate Chart to highlight operational efforts of sev2 tickets in past 3 months';
  } else {
    return 'Example: Analyze key metrics and trends, or What insights can you provide about the current data patterns?';
  }
};

export type MultiselectChangeDetail = MultiselectProps.MultiselectChangeDetail;

type SetStateAction<T> = T | ((prevState: T) => T);

// Base interface containing common properties shared across all handlers
export interface BaseProps {
  pageType: string;
  filters: string;
  aggregatedData: string;
  eventsApiContext: any;
  authContext: any;
  setCustomError: (error: string | null) => void;
  setRequestStartTime: (value: number | null) => void;
}

// Interface for prompt optimization and handling
export interface PromptHandlerProps extends BaseProps {
  userPrompt: string;
  optimizedPrompt?: string;
  setOptimizedPrompt: (prompt: string) => void;
  setSuggestions: (suggestions: string[]) => void;
  setIsOptimizing: (value: boolean) => void;
  validatePromptContent: (prompt: string) => { isValid: boolean; error?: string };
}

// Interface for summary generation and management
export interface SummaryHandlerProps extends BaseProps {
  summaryMode: 'new' | 'enhance';
  originalSummary: string;
  setIsGeneratingCustom: (value: boolean) => void;
  setSummary: (value: string) => void;
  setSummaryId: (value: string) => void;
}

// Interface for chart generation and management
export interface ChartHandlerProps extends BaseProps {
  setIsGeneratingCustomChart: (value: boolean) => void;
  setCustomCharts: (value: any[]) => void;
  setShowCustomCharts: (show: boolean) => void;
}

// Interface for action selection and management
export interface ActionSelectionProps {
  selectedActions: string[];
  setSelectedActions: (actions: SetStateAction<string[]>) => void;
  completedActions: string[];
  setCompletedActions: (actions: SetStateAction<string[]>) => void;
}

// Combined interface for components that need all functionality
export interface ActionHandlerProps extends PromptHandlerProps, SummaryHandlerProps, ChartHandlerProps, ActionSelectionProps {
  handleGenerateCustomSummary: () => Promise<void>;
  handleGenerateCustomCharts: () => Promise<void>;
}

export const validatePromptContent = (prompt: string): { isValid: boolean; error?: string; hasRestrictedContent?: boolean } => {
  const lowerPrompt = prompt.toLowerCase();

  // Check for restricted content using word boundary regex
  const hasRestrictedContent = RESTRICTED_PROMPT_KEYWORDS.some((keyword) => {
    // Use word boundary \b to match whole words only
    const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(lowerPrompt);
  });

  if (hasRestrictedContent) {
    return {
      isValid: false,
      hasRestrictedContent: true,
      error: 'Please ask questions related to business reporting, tickets, user metrics, or application performance only.'
    };
  }

  // Check if prompt contains at least one business-related keyword using word boundary regex
  const hasValidContent = VALID_PROMPT_KEYWORDS.some((keyword) => {
    // Use word boundary \b to match whole words only
    const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(lowerPrompt);
  });

  if (!hasValidContent) {
    return {
      isValid: false,
      error:
        'Please ask questions related to business reporting data such as tickets, user metrics, application performance, or operational insights.'
    };
  }

  return { isValid: true };
};

export const handleOptimizePrompt = async (props: PromptHandlerProps) => {
  const { userPrompt, pageType, filters, eventsApiContext, authContext, setIsOptimizing, setCustomError, setOptimizedPrompt, setSuggestions } = props;
  if (!userPrompt.trim()) {
    setCustomError('Please enter a prompt to optimize.');
    return;
  }

  setIsOptimizing(true);
  setCustomError(null);

  const { abortController, cleanup } = createTimeoutController(REQUEST_TIMEOUT);

  try {
    const dataType = pageType.toLowerCase().includes('ticket')
      ? 'tickets, issues, and support metrics'
      : 'user engagement, application usage, and performance metrics';

    const optimizationData = JSON.stringify({
      originalPrompt: userPrompt,
      dataType: dataType,
      availableFilters: filters,
      pageContext: pageType
    });

    // Use the existing backend optimization functionality
    const optimizationPageType = `${pageType}_OPTIMIZATION`;

    const optimizationResponse = await fetchPageSummary(
      uuidv4(),
      optimizationPageType,
      filters,
      optimizationData,
      eventsApiContext,
      authContext.Alias || 'unknown'
    );

    cleanup();

    try {
      ApiErrorHandler.validateResponse(optimizationResponse.message);
      const parsedResponse = ApiErrorHandler.parseResponse(optimizationResponse.message);

      if (!parsedResponse.success) {
        throw new Error(parsedResponse.error?.message || 'Failed to parse optimization response');
      }

      const responseText = parsedResponse.data || optimizationResponse.message;

      // Parse the optimization response
      const optimizedMatch = responseText.match(/Optimized Prompt:\s*(.+?)(?:\n\n|$)/s);
      const suggestionsMatch = responseText.match(/Suggestions:\s*((?:\n?-\s*.+)+)/s);

      if (optimizedMatch) {
        const optimizedPromptText = optimizedMatch[1].trim();
        setOptimizedPrompt(optimizedPromptText);

        // Parse suggestions if available
        let parsedSuggestions: string[] = [];
        if (suggestionsMatch) {
          parsedSuggestions = suggestionsMatch[1]
            .split('\n')
            .filter((line) => line.trim().startsWith('-'))
            .map((line) => line.replace(/^-\s*/, '').trim())
            .filter((suggestion) => suggestion.length > 0);
        }

        // Fallback suggestions if none were parsed
        if (parsedSuggestions.length === 0) {
          parsedSuggestions = [
            'Your prompt has been optimized by AI for better business insights',
            'The enhanced prompt focuses on specific metrics and quantitative analysis',
            'Try generating analysis with the improved prompt for more detailed results'
          ];
        }

        setSuggestions(parsedSuggestions);

        logger.debug('AI-powered prompt optimization successful:', {
          original: userPrompt,
          optimized: optimizedPromptText,
          suggestions: parsedSuggestions
        });
      } else {
        // If we can't parse the expected format, use the entire response as optimized prompt
        const fallbackOptimized =
          responseText.length > userPrompt.length
            ? responseText
            : `${userPrompt} - Please provide detailed analysis with specific metrics, trends, and actionable business insights based on the available data.`;

        setOptimizedPrompt(fallbackOptimized);
        setSuggestions([
          'AI has enhanced your prompt for better business reporting results',
          'The optimized version focuses on quantitative analysis and specific insights',
          'Use the improved prompt to get more detailed and actionable analysis'
        ]);

        logger.debug('AI optimization completed with fallback parsing:', {
          original: userPrompt,
          optimized: fallbackOptimized
        });
      }
    } catch (parseError) {
      console.error('Error parsing AI optimization response:', parseError);
      // Provide a meaningful fallback if AI response parsing fails
      const fallbackOptimized = `${userPrompt} - Please provide detailed analysis with specific metrics, percentages, trends over time, and actionable business insights based on the available data.`;
      setOptimizedPrompt(fallbackOptimized);
      setSuggestions([
        'AI optimization completed with basic enhancement',
        'The enhanced prompt requests more specific metrics and insights',
        'Try the optimized version for better analysis results'
      ]);
    }
  } catch (err) {
    cleanup();
    console.error('Error calling AI optimization service:', err);

    // Provide a meaningful fallback optimization if the AI service fails
    const fallbackOptimized = `${userPrompt} - Focus on specific metrics, trends, and actionable insights with quantitative analysis.`;
    setOptimizedPrompt(fallbackOptimized);
    setSuggestions([
      'AI optimization service is temporarily unavailable',
      'Your prompt has been enhanced with a basic improvement',
      'Try being more specific about metrics and timeframes for better results'
    ]);

    setCustomError('AI optimization service encountered an issue, but your prompt has been enhanced with basic improvements.');
  } finally {
    setIsOptimizing(false);
  }
};

export const handleGenerateCustomSummary = async (props: SummaryHandlerProps & Pick<PromptHandlerProps, 'userPrompt' | 'optimizedPrompt'>) => {
  const {
    userPrompt,
    optimizedPrompt,
    pageType,
    filters,
    aggregatedData,
    eventsApiContext,
    authContext,
    summaryMode,
    originalSummary,
    setIsGeneratingCustom,
    setCustomError,
    setRequestStartTime,
    setSummary,
    setSummaryId
  } = props;
  const promptToUse = optimizedPrompt || userPrompt;

  if (!promptToUse.trim()) {
    setCustomError('Please enter a prompt or optimize your current prompt first.');
    return;
  }

  // Validate prompt content
  const validation = validatePromptContent(promptToUse);
  if (!validation.isValid) {
    setCustomError(validation.error || 'Invalid prompt content.');
    return;
  }

  setIsGeneratingCustom(true);
  setCustomError(null);
  const startTime = Date.now();
  setRequestStartTime(startTime);

  // Create AbortController for consistent timeout handling
  const { abortController, cleanup } = createTimeoutController(REQUEST_TIMEOUT);

  try {
    let finalPrompt = promptToUse;

    if (summaryMode === 'enhance') {
      // Truncate original summary if too long to reduce token count
      const truncatedSummary = originalSummary.replace(/<[^>]*>/g, '').substring(0, 500);
      finalPrompt = ENHANCE_SUMMARY_PROMPT_TEMPLATE.replace('{customPrompt}', promptToUse).replace('{truncatedSummary}', truncatedSummary);
    } else {
      finalPrompt = AI_OPTIMIZATION_PROMPT_TEMPLATE.replace('{customPrompt}', promptToUse);
    }

    // Use the same data source as the original summary by calling fetchPageSummary
    // with a custom pageType that indicates this is a custom prompt request
    const customPageType = `${pageType}_CUSTOM_PROMPT`;
    const customAggregatedData = JSON.stringify({
      originalData: aggregatedData,
      customPrompt: finalPrompt,
      summaryMode: summaryMode,
      originalSummary: summaryMode === 'enhance' ? originalSummary : null
    });

    const customSummaryText = await fetchPageSummary(
      uuidv4(),
      customPageType,
      filters,
      customAggregatedData,
      eventsApiContext,
      authContext.Alias || 'unknown'
    );

    cleanup();

    // Use robust error handling and response parsing
    try {
      ApiErrorHandler.validateResponse(customSummaryText.message);
      const parsedResponse = ApiErrorHandler.parseResponse(customSummaryText.message);

      if (!parsedResponse.success) {
        setCustomError(parsedResponse.error?.message || 'Failed to generate custom summary.');
        return;
      }

      const finalSummary = parsedResponse.data || customSummaryText.message;
      setSummary(finalSummary);
      setSummaryId(customSummaryText.summaryId);

      // Skip caching for fresh results each time
      logger.debug('Custom summary generated successfully (no caching)');

      // Log performance metrics
      const responseTime = Date.now() - startTime;
      logger.debug(`Custom summary generated in ${responseTime}ms`);
    } catch (validationError) {
      if (validationError instanceof ApiResponseError) {
        setCustomError(validationError.message);
      } else {
        setCustomError('Failed to process the AI response. Please try again.');
      }
      return;
    }
  } catch (err) {
    cleanup();
    console.error('Error generating custom summary:', err);

    // Use robust error handling with timeout-aware messages
    const errorMessage = handleTimeoutError(err, 'Request timed out (30s). Please try again with a simpler prompt.');
    setCustomError(errorMessage);
  } finally {
    setIsGeneratingCustom(false);
    setRequestStartTime(null);
  }
};

export const handleGenerateCustomCharts = async (props: ChartHandlerProps & Pick<PromptHandlerProps, 'userPrompt'>) => {
  const {
    userPrompt,
    pageType,
    filters,
    aggregatedData,
    eventsApiContext,
    authContext,
    setIsGeneratingCustomChart,
    setCustomError,
    setRequestStartTime,
    setCustomCharts,
    setShowCustomCharts
  } = props;
  if (!userPrompt.trim()) {
    setCustomError('Please enter a prompt or optimize your current prompt first.');
    return;
  }

  // Validate prompt content
  const validation = validatePromptContent(userPrompt);
  if (!validation.isValid) {
    setCustomError(validation.error || 'Invalid prompt content.');
    return;
  }

  setIsGeneratingCustomChart(true);
  setCustomError(null);
  setRequestStartTime(Date.now());
  const { abortController, cleanup } = createTimeoutController(REQUEST_TIMEOUT);

  try {
    const customPageType = `${pageType}_CUSTOM_CHARTS`;
    const customAggregatedData = JSON.stringify({
      originalData: aggregatedData,
      chartRequest: {
        dataType: pageType.toLowerCase().includes('ticket') ? 'tickets' : 'user_metrics',
        chartTypes: ['line', 'bar', 'pie']
      },
      customPrompt: userPrompt
    });

    const chartResponse = await fetchPageSummary(
      uuidv4(),
      customPageType,
      filters,
      customAggregatedData,
      eventsApiContext,
      authContext.Alias || 'unknown'
    );

    cleanup();

    try {
      ApiErrorHandler.validateResponse(chartResponse.message);

      // Parse and validate chart data
      const validCharts = parseChartData(chartResponse.message);
      logger.debug('Valid charts found:', { count: validCharts.length });

      // Transform charts to the appropriate format
      const transformedCharts = validCharts
        .map((chartData) => {
          if (chartData.chartType === 'pie') {
            return pieChartTransformer.transform(chartData as ChartData & Record<string, unknown>);
          } else if (chartData.xAxis && chartData.yAxis) {
            try {
              return barLineChartTransformer.transform(chartData);
            } catch (error) {
              logger.error(
                `Failed to transform bar/line chart: ${chartData.chartTitle || 'Unknown'}`,
                error instanceof Error ? error : new Error(String(error))
              );
              return null;
            }
          }
          return null;
        })
        .filter((chart): chart is ChartProps => chart !== null);

      if (transformedCharts.length === 0) {
        setCustomError('Failed to generate any valid charts from the provided data. Please try a different prompt.');
        setShowCustomCharts(false);
        logger.error('No valid charts could be generated from the data');
        return;
      }

      logger.debug('Transformed charts:', { charts: transformedCharts });
      setCustomCharts(transformedCharts);
      setShowCustomCharts(true);
      setCustomError(null);
      logger.debug('Custom chart generated successfully');
    } catch (validationError) {
      if (validationError instanceof ApiResponseError) {
        setCustomError(validationError.message);
      } else {
        setCustomError('Failed to process the chart generation response. Please try again.');
      }
      return;
    }
  } catch (err) {
    cleanup();
    console.error('Error generating custom charts:', err);
    const errorMessage = handleTimeoutError(err, 'Request timed out (30s). Please try again.');
    setCustomError(errorMessage);
  } finally {
    setIsGeneratingCustomChart(false);
    setRequestStartTime(null);
  }
};

export const resetToOriginalSummary = ({
  originalSummary,
  originalSummaryId,
  setSummary,
  setSummaryId,
  setUserPrompt,
  setOptimizedPrompt,
  setSuggestions,
  setCustomError,
  setShowCustomPrompt,
  setShowCustomCharts,
  setCompletedActions
}: {
  originalSummary: string;
  originalSummaryId: string;
  setSummary: (summary: string) => void;
  setSummaryId: (id: string) => void;
  setUserPrompt: (prompt: string) => void;
  setOptimizedPrompt: (prompt: string) => void;
  setSuggestions: (suggestions: string[]) => void;
  setCustomError: (error: string | null) => void;
  setShowCustomPrompt: (show: boolean) => void;
  setShowCustomCharts: (show: boolean) => void;
  setCompletedActions: (actions: string[]) => void;
}) => {
  setSummary(originalSummary);
  setSummaryId(originalSummaryId);
  setUserPrompt('');
  setOptimizedPrompt('');
  setSuggestions([]);
  setCustomError(null);
  setShowCustomPrompt(false);
  setShowCustomCharts(false);
  setCompletedActions([]);
};

export const handleSelectionChange = (
  event: { detail: MultiselectChangeDetail },
  props: ActionSelectionProps & Pick<PromptHandlerProps, 'setOptimizedPrompt' | 'setSuggestions'>
) => {
  const { selectedActions, setSelectedActions, completedActions, setCompletedActions, setOptimizedPrompt, setSuggestions } = props;
  const newSelectedActions = Array.from(event.detail.selectedOptions)
    .map((option: { value?: string }) => option.value ?? '')
    .filter((value): value is string => value !== undefined);

  // Remove actions from completedActions if they were deselected
  const deselectedActions = selectedActions.filter((action: string) => !newSelectedActions.includes(action));
  if (deselectedActions.length > 0) {
    setCompletedActions((prev: string[]) => prev.filter((action: string) => !deselectedActions.includes(action)));
  }

  setSelectedActions(newSelectedActions);
};

export const handleBlur = async ({
  selectedActions,
  completedActions,
  setCompletedActions,
  setShowCustomCharts,
  setCustomError,
  userPrompt,
  validatePromptContent,
  handleGenerateCustomSummary,
  handleGenerateCustomCharts
}: ActionHandlerProps) => {
  if (!selectedActions.includes('generateCharts')) {
    setShowCustomCharts(false);
  }

  // Validate prompt content before executing actions
  if (selectedActions.length > 0) {
    const validation = validatePromptContent(userPrompt);
    if (!validation.isValid) {
      setCustomError(validation.error || 'Invalid prompt content.');
      return;
    }
  }

  // Find only the new actions that haven't been completed yet
  const newActions = selectedActions.filter((action) => !completedActions.includes(action));

  // Run actions in parallel if multiple are selected
  const actionPromises = newActions.map((action) => {
    if (action === 'generateSummary') {
      return handleGenerateCustomSummary().then(() => action);
    }
    if (action === 'generateCharts') {
      return handleGenerateCustomCharts().then(() => action);
    }
    return Promise.resolve(action);
  });

  // Wait for all actions to complete
  const completedActionResults = await Promise.all(actionPromises);

  // Add completed actions
  setCompletedActions((prev) => [...prev, ...completedActionResults]);
};
